import cx from 'classnames';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import Linkifier from 'react-linkifier';
import { Button, Feed, Icon, Image, Segment } from 'semantic-ui-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { getString } from '../../utils';
import { avatarWithFallbackWhenMissing } from '../../utils/avatarUtils';
import { dateFNSLocale } from '../../utils/date';
import Images from '../../utils/Images';
import { sweetConfirmAlert } from '../../utils/popupUtils';
import './ChatFeed.scss';

const { anonymousUserName = '' } = window.__DATA__;

function formatLastName(name) {
  return name ? `${name.slice(0, 1)}.` : '';
}

function shortName(profile) {
  if (!profile?.firstName && !profile?.firstName) return anonymousUserName;
  return `${profile?.firstName} ${formatLastName(profile?.lastName)}`;
}
function longName(profile) {
  return `${profile?.firstName || ''} ${profile?.lastName || ''}`;
}

function formatProfileName(profile) {
  return profile?.isSponsor ? longName(profile) : shortName(profile);
}

const ChatFeed = ({ feedHeader, messages, me, sponsorImage, onEnterVisio, onDelete }) => {
  useAutoRefresh(60 * 1000); // Refresh messages times...

  const removeMessage = useCallback(
    async (id) => {
      if (!onDelete) return;
      const response = await sweetConfirmAlert({
        title: getString('chat.delete-message'),
        confirmButtonText: getString(`alert.confirm`),
        cancelButtonText: getString(`alert.cancel`),
      });
      if (!response) return;
      onDelete(id);
    },
    [onDelete],
  );

  return (
    <Feed className="feed--chat Chat--Feed" key="Chat--Feed">
      <div className="Chat--PushDown" />
      {feedHeader && feedHeader.text && (
        <div className="Chat--HeaderMessage">{feedHeader.text}</div>
      )}
      {messages &&
        messages.map(({ type, visio, text, emitter, profile, createdAt }) => {
          const isSelf = emitter === me;
          const avatar = (
            <Feed.Label>
              <Image src={avatarWithFallbackWhenMissing(profile?.thumbnail)} avatar size="tiny" />
            </Feed.Label>
          );

          return (
            <Feed.Event
              className={cx('Chat--Message', {
                [`event--type-${type}`]: !!type,
                'event--self': isSelf,
                'event--other': !isSelf,
                'event--sponsor': profile?.isSponsor,
              })}
            >
              {avatar}
              <Feed.Content>
                <Feed.Summary>
                  {profile?.isSponsor && sponsorImage && (
                    <img
                      className="image--sponsor"
                      alt="sponsor"
                      {...Images.srcSet.maxWidth(sponsorImage, 200)}
                    />
                  )}
                  {/* TODO : simplify once ovh ends name is for retro compatibility */}
                  <span>{profile?.name ? profile?.name : formatProfileName(profile)}</span>
                </Feed.Summary>
                {text &&
                  text.map(({ id, text: rawText }) => (
                    <Feed.Content key={`message-${id}`} className="Chat--Feed__TextContent">
                      <Linkifier target="_blank">
                        <p>{rawText} </p>
                        {isSelf && onDelete && (
                          <Button
                            circular
                            negative
                            compact
                            size="mini"
                            className="Chat--Feed__DeleteButton"
                            onClick={() => removeMessage(id)}
                            icon="delete"
                          />
                        )}
                      </Linkifier>
                    </Feed.Content>
                  ))}
                {visio && !!onEnterVisio && (
                  <Feed.Content className="Chat--Feed__VisioRequest">
                    <Segment.Group raised style={{ width: 280 }}>
                      <Segment>
                        <Icon bordered inverted size="large" color="blue" name="call" />{' '}
                        {isSelf ? 'Appel sortant' : 'Appel entrant'}
                      </Segment>
                      <Segment textAlign="right">
                        {visio.state === 'started' && (
                          <Button primary onClick={() => onEnterVisio(visio)}>
                            Rejoindre l'appel
                          </Button>
                        )}
                        {visio.state !== 'started' && <Button disabled>Appel terminé</Button>}
                      </Segment>
                    </Segment.Group>
                  </Feed.Content>
                )}
                <Feed.Meta>
                  <Feed.Date
                    content={formatDistanceToNow(createdAt, {
                      addSuffix: true,
                      locale: dateFNSLocale(),
                    })}
                  />
                </Feed.Meta>
              </Feed.Content>
            </Feed.Event>
          );
        })}
    </Feed>
  );
};

ChatFeed.defaultProps = {
  feedHeader: undefined,
  sponsorImage: undefined,
  onDelete: undefined,
  onEnterVisio: undefined,
};

const profilePropTypes = {
  isSponsor: PropTypes.bool,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  name: PropTypes.string, // TODO : retro compatibility should be deleted once ovh ends
  thumbnail: PropTypes.object,
};

const messagePropTypes = {
  id: PropTypes.string.isRequired,
  createdAt: PropTypes.oneOf([PropTypes.instanceOf(Date), PropTypes.string]).isRequired,
  emitter: PropTypes.string.isRequired,
  profile: PropTypes.shape(profilePropTypes),
};

const textMessagePropTypes = {
  ...messagePropTypes,
  text: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

const visioRequestPropTypes = {
  ...messagePropTypes,
  type: PropTypes.exact('visio-request'),
  session: PropTypes.shape({
    type: PropTypes.string,
    sessionId: PropTypes.string,
  }),
};

ChatFeed.propTypes = {
  feedHeader: PropTypes.shape({
    text: PropTypes.string,
  }),
  messages: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape(textMessagePropTypes),
      PropTypes.shape(visioRequestPropTypes),
    ]),
  ).isRequired,
  me: PropTypes.string.isRequired,
  sponsorImage: PropTypes.shape({ uri: PropTypes.string }),
  onDelete: PropTypes.func,
  onEnterVisio: PropTypes.func,
};

export default ChatFeed;
